<template>
  <AuthLayout
    data-testid="forgot-password"
    :loading="loading"
    :disabled="loading"
  >
    <AuthLoginLogo />

    <form
      v-if="stage === 1"
      class="forgot-password__form"
      data-testid="forgot-password__form"
      novalidate
      @submit.prevent="forgotPassword"
    >
      <header class="forgot-password__header">
        <h1 class="forgot-password__title" data-testid="forgot-password__title">
          {{ copy.RESET_HEADING }}
        </h1>
        <p class="forgot-password__subtitle" data-testid="forgot-password__subtitle">
          {{ copy.RESET_SUBTITLE }}
        </p>
      </header>
      <div class="forgot-password__fields">
        <div class="forgot-password__field">
          <label class="forgot-password__label" for="forgot-password-email">
            {{ copy.WORK_EMAIL_LABEL }}
          </label>
          <input
            id="forgot-password-email"
            v-model="form.username"
            class="forgot-password__input"
            :class="{ 'forgot-password__input--error': Boolean(error) }"
            type="email"
            name="email"
            autocomplete="username"
            :placeholder="copy.WORK_EMAIL_PLACEHOLDER"
            :aria-invalid="Boolean(error)"
            :aria-describedby="error ? 'forgot-password-email-error' : null"
            :disabled="loading"
            autofocus
            data-testid="forgot-password__email"
          />
          <p
            v-if="error"
            id="forgot-password-email-error"
            class="forgot-password__field-error"
            role="alert"
            data-testid="forgot-password__email-error"
          >
            {{ error }}
          </p>
        </div>
        <button
          class="forgot-password__primary"
          type="submit"
          :disabled="!canSubmit"
          data-testid="forgot-password__reset-existing"
        >
          {{ copy.RESET_EXISTING }}
        </button>
        <button
          class="forgot-password__secondary"
          type="button"
          :disabled="!canSubmit"
          data-testid="forgot-password__reset-new"
          @click="resendPassword"
        >
          {{ copy.RESET_NEW }}
        </button>
        <button
          class="forgot-password__text"
          type="button"
          data-testid="forgot-password__cancel"
          @click="goBack"
        >
          {{ copy.RESET_CANCEL }}
        </button>
      </div>
    </form>

    <div
      v-else-if="stage === 2"
      class="forgot-password__success"
      data-testid="forgot-password__success"
    >
      <h1
        class="forgot-password__title"
        data-testid="forgot-password__success-title"
      >
        {{ copy.CHECK_INBOX_HEADING }}
      </h1>
      <p
        class="forgot-password__success-body"
        data-testid="forgot-password__success-body"
      >
        {{ inboxBody }}
      </p>
      <div
        class="forgot-password__notice"
        data-testid="forgot-password__notice"
      >
        <span class="forgot-password__notice-mark" aria-hidden="true">i</span>
        <p class="forgot-password__notice-text">
          {{ copy.CHECK_INBOX_NOTICE }}
        </p>
      </div>
      <div class="forgot-password__actions">
        <button
          class="forgot-password__secondary"
          type="button"
          :disabled="loading"
          data-testid="forgot-password__resend"
          @click="resendLastReset"
        >
          {{ copy.RESEND_EMAIL }}
        </button>
        <button
          class="forgot-password__text"
          type="button"
          data-testid="forgot-password__use-different-email"
          @click="useDifferentEmail"
        >
          {{ copy.USE_DIFFERENT_EMAIL }}
        </button>
      </div>
    </div>

    <div v-else class="forgot-password__fallback" data-testid="forgot-password__noaccess">
      <noaccess class="pb-3" />
    </div>
  </AuthLayout>
</template>

<script>
import noaccess from '@/views/common/NoAccess';
import constants from '../../plugins/constants';
import { isValidWorkEmail } from './utils/authLoginUtils';
import AuthLayout from './components/AuthLayout.vue';
import AuthLoginLogo from './components/AuthLoginLogo.vue';

const { AUTH } = constants;

export default {
  name: 'ForgotPassword',
  components: {
    noaccess,
    AuthLayout,
    AuthLoginLogo,
  },
  props: {
    props: { type: Object, default: () => ({}) },
  },
  data: () => ({
    stage: 1,
    loading: false,
    form: {
      username: '',
    },
    message: '',
    error: '',
    lastResetAction: 'forgotPassword',
    copy: AUTH,
  }),
  computed: {
    canSubmit() {
      return Boolean(this.form.username) && !this.loading;
    },
    inboxBody() {
      return this.copy.CHECK_INBOX_BODY.replace(
        '{email}',
        this.form.username || '',
      );
    },
  },
  watch: {
    'form.username'(value) {
      this.error = '';
      const normalized = String(value || '').toLowerCase();
      if (value !== normalized) {
        this.form.username = normalized;
      }
    },
  },
  created() {
    this.$store.commit('addLoading');
    this.$nextTick(() => {
      this.$store.commit('removeLoading');
    });
  },
  beforeDestroy() {
    this.stage = 1;
    this.loading = false;
    this.form = { username: '' };
    this.message = '';
    this.error = '';
    this.lastResetAction = 'forgotPassword';
  },
  methods: {
    goBack() {
      this.$router.push('/');
    },
    useDifferentEmail() {
      this.stage = 1;
      this.error = '';
      this.message = '';
    },
    applyResetResult(result) {
      console.log('[ForgotPassword]:', result);
      if (result?.success) {
        this.message = result.message || '';
        this.stage = 2;
        return;
      }
      this.stage = 1;
      this.error =
        result?.message && result.message !== this.copy.RESET_GENERIC_ERROR
          ? result.message
          : this.copy.GENERIC_DISCOVER_ERROR;
    },
    async runReset(action) {
      const email = String(this.form.username || '').trim().toLowerCase();
      if (!email || this.loading) return;
      if (!isValidWorkEmail(email)) {
        this.error = this.copy.INVALID_WORK_EMAIL;
        return;
      }
      this.form.username = email;
      this.error = '';
      this.loading = true;
      this.lastResetAction = action;
      console.log('[ForgotPassword]:', { action });
      try {
        const result = await this.$store.dispatch(`auth/${action}`, email);
        this.applyResetResult(result);
      } finally {
        this.loading = false;
      }
    },
    async forgotPassword() {
      await this.runReset('forgotPassword');
    },
    async resendPassword() {
      await this.runReset('resendPassword');
    },
    async resendLastReset() {
      await this.runReset(this.lastResetAction || 'forgotPassword');
    },
  },
};
</script>

<style lang="scss" scoped>
@import './ForgotPassword.scss';
</style>
